import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { HiUser, HiLogout, HiCog, HiMenu } from 'react-icons/hi';
import PreferencesModal from '../PreferencesModal';
import hexiumLogo from '../../../assets/hexium-logo.png';
import { 
  HeaderContainer, 
  LeftSection,
  Logo,
  BrandSection,
  PageInfo,
  RightSection,
  UserContainer,
  UserInfo,
  UserAvatar,
  ProfileMenu,
  MenuHeader,
  MenuItem,
  MobileMenuButton
} from './header.styles';

interface HeaderProps {
  onToggleSidebar?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onToggleSidebar }) => {
  const { user, signOut } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const avatarRef = useRef<HTMLDivElement>(null);
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        avatarRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !avatarRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get page info based on current route with translations
  const getPageInfo = () => {
    // Verificar se a função t está disponível
    if (!t || typeof t !== 'function') {
      return { title: user?.nameOrganization || 'LIA', subtitle: 'Plataforma Legal com IA' };
    }

    const path = location.pathname;
    
    if (path === '/') return { title: t('header.dashboard'), subtitle: t('subtitle.dashboard') };
    if (path.startsWith('/users')) return { title: t('header.users'), subtitle: t('subtitle.users') };
    if (path.startsWith('/organizations')) return { title: t('header.organizations'), subtitle: t('subtitle.organizations') };
    if (path.startsWith('/compliance')) return { title: t('header.chat'), subtitle: t('subtitle.chat') };
    if (path.startsWith('/rules')) return { title: t('header.rules'), subtitle: t('subtitle.rules') };
    if (path.startsWith('/results')) return { title: t('header.results'), subtitle: t('subtitle.results') };
    if (path.startsWith('/account')) return { title: t('header.account'), subtitle: t('subtitle.account') };
    
    return { title: user?.nameOrganization || 'LIA', subtitle: t('subtitle.default') };
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user?.name) return user?.email?.slice(0, 2).toUpperCase() || 'U';
    return user.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  };

  // Função de toggle removida - apenas dark theme

  const handleLogout = () => {
    signOut();
    navigate('/login');
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const navigateToAccount = () => {
    navigate('/account');
    setIsMenuOpen(false);
  };

  const openPreferences = () => {
    setIsPreferencesOpen(true);
    setIsMenuOpen(false);
  };

  const pageInfo = getPageInfo();
  
  // Log para debug
  console.log("Dados do usuário no Header:", user);
  console.log("Nome da organização no Header:", user?.nameOrganization);
  
  // Alert para mostrar o nome no header (apenas uma vez por carregamento)
  if (user?.nameOrganization && user.nameOrganization !== "LIA") {
    // Verificar se já mostrou o alert para evitar spam
    const alertKey = `header-org-alert-${user.nameOrganization}`;
    if (!sessionStorage.getItem(alertKey)) {
      //alert(`Header carregado com organização: ${user.nameOrganization}`);
      sessionStorage.setItem(alertKey, 'shown');
    }
  }
  
  return (
    <HeaderContainer>
      <LeftSection>
        <MobileMenuButton onClick={onToggleSidebar} className="show-mobile">
          {HiMenu({ size: 20 })}
        </MobileMenuButton>
        
        {/* <PageInfo>
          <h1 className="page-title">{pageInfo.title}</h1>
          <p className="page-subtitle">{pageInfo.subtitle}</p>
        </PageInfo> */}
      </LeftSection>

      {/*
        MARCA DO PRODUTO — logo à esquerda, nome à direita, no CENTRO do cabeçalho.

        Fica em seção própria porque ela é a coluna do meio da grade. Enquanto vivia dentro da
        `LeftSection`, era mais um item de uma fila alinhada à esquerda, e não havia como
        centralizá-la sem tirá-la de lá.

        Este espaço mostrava `user?.nameOrganization` — o nome da ORGANIZAÇÃO de quem está logado, o
        que fazia aparecer "Casanova". Marca de produto e nome de inquilino são coisas diferentes: o
        cabeçalho identifica de quem é o sistema, não quem o está usando.

        ⚠️ Efeito colateral consciente: o nome da organização deixa de aparecer. Numa plataforma
        multi-inquilino isso é informação útil — saber em qual organização se está trabalhando. Se
        fizer falta, o lugar natural é ao lado do menu do usuário, à direita.
      */}
      <BrandSection>
        {/*
          `to="/"` é a página principal — a mesma rota da seção "dashboard" em `navegacao/secoes.tsx`.

          O `alt` da imagem fica VAZIO de propósito: a imagem e o texto "Hexium Tech" ao lado dizem a
          mesma coisa, e descrever as duas faria o leitor de tela anunciar "Hexium Tech Hexium Tech".
          O `aria-label` no link é o que nomeia o destino.
        */}
        <Logo to="/" aria-label="Hexium Tech — ir para a página principal">
          <img src={hexiumLogo} alt="" className="logo-icon" />
          <span>Hexium Tech</span>
        </Logo>
      </BrandSection>

      <RightSection>
        <UserContainer>
          <UserInfo>
            <div className="user-name">{user?.name || user?.email?.split('@')[0]}</div>
            <div className="user-email">{user?.email}</div>
          </UserInfo>
          
          <UserAvatar ref={avatarRef} onClick={toggleMenu}>
            {getUserInitials()}
          </UserAvatar>
          
          <ProfileMenu ref={menuRef} $isOpen={isMenuOpen}>
            <MenuHeader>
              <div className="user-name">{user?.name || user?.email?.split('@')[0]}</div>
              <div className="user-email">{user?.email}</div>
            </MenuHeader>
            
            <MenuItem onClick={navigateToAccount}>
              {HiUser({ size: 18 })}
              <span>{t && typeof t === 'function' ? t('header.accountSettings') : 'Configurações da Conta'}</span>
            </MenuItem>
            
            <MenuItem onClick={openPreferences}>
              {HiCog({ size: 18 })}
              <span>{t && typeof t === 'function' ? t('header.preferences') : 'Preferências'}</span>
            </MenuItem>
            
            <MenuItem onClick={handleLogout} className="danger">
              {HiLogout({ size: 18 })}
              <span>{t && typeof t === 'function' ? t('header.signOut') : 'Sair'}</span>
            </MenuItem>
          </ProfileMenu>
        </UserContainer>
      </RightSection>
      
      <PreferencesModal 
        isOpen={isPreferencesOpen} 
        onClose={() => setIsPreferencesOpen(false)} 
      />
    </HeaderContainer>
  );
};

export default Header;
